import React, { useEffect, useState, useRef } from 'react';
import { auth } from '../../../../firebase/config';
import { useAppSelector } from '../../../../store';
import Button from '../../../../components/Button';
import TextField from '../../../../components/TextField';
import Toast from '../../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiAlertCircle, FiFileText, FiMail, FiDownload, FiFile, FiEdit2, FiSave, FiX, FiCalendar, FiClipboard, FiRefreshCw } from 'react-icons/fi';
import Modal from '../../../../components/Modal';
import Icon from '../../../../components/Icon';
import { fetchSurveys, fetchComplaints, updateSurvey, getCompanyId, createSurvey, type Survey, type Complaint } from '../service/surveyService';
import { db } from '../../../../firebase/config';
import { getDocs, collection } from 'firebase/firestore';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { sendEmailsWithErrorHandling, validateEmails, formatSurveyEmailBody, formatComplaintEmailBody } from '../../../../api/emailService';
import Table, { Column } from '../../../../components/Table';

export default function Surveys() {
    const [activeTab, setActiveTab] = useState<'surveys' | 'complaints'>('surveys');
    const [surveys, setSurveys] = useState<Survey[]>([]);
    const [complaints, setComplaints] = useState<Complaint[]>([]);
    const [filteredSurveys, setFilteredSurveys] = useState<Survey[]>([]);
    const [filteredComplaints, setFilteredComplaints] = useState<Complaint[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
        show: false,
        message: '',
        type: 'success',
    });
    const [loading, setLoading] = useState(true);
    const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
    const [selectedComplaint, setSelectedComplaint] = useState<Complaint | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailRecipients, setEmailRecipients] = useState('');
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [dateFilter, setDateFilter] = useState<'all' | 'daily' | 'weekly' | 'monthly'>('all');
    const [isEditing, setIsEditing] = useState(false);
    const [editedSurvey, setEditedSurvey] = useState<Partial<Survey> | null>(null);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [branchSearchTerm, setBranchSearchTerm] = useState('');
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const [psoName, setPsoName] = useState<string>('');
    const [formData, setFormData] = useState({
        surveyId: '',
        dateOfVisit: '',
        branchId: '',
        jointVisit: '',
        jointTeams: '',
        status: 'Pending',
        remarks: '',
        cctv: { status: '', issues: '', otherIssues: '' },
        bams: { status: '', issues: '', otherIssues: '' },
        guarding: { status: '', issues: '', otherIssues: '' },
        customIssues: [] as Array<{ heading: string; description: string }>
    });

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role'); // 'bank' or 'pso'
    const isBankUser = userRole === 'bank';
    const pso = useAppSelector((state: any) => state.user.pso);
    const designation = (pso?.designation || '').replace(/_/g, ' ').toLowerCase();
    const isMISIncharge = designation.includes('mis');
    const branchDropdownRef = useRef<HTMLDivElement>(null);

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    // Reset page on tab/filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchTerm, dateFilter]);

    const getStatusBadge = (status?: string) => {
        if (!status) {
            return (
                <span className="inline-flex rounded-full px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                    N/A
                </span>
            );
        }
        const statusLower = status.toLowerCase();
        let bgColor = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';

        if (statusLower === 'resolved') {
            bgColor = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        } else if (statusLower === 'pending') {
            bgColor = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        }

        return (
            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${bgColor}`}>
                {status}
            </span>
        );
    };

    const surveyColumns: Column<Survey>[] = [
        { header: 'Survey ID', accessor: 'surveyId', width: '15%', render: (s) => <span className="font-medium text-gray-900 dark:text-gray-100">{s.surveyId || 'N/A'}</span> },
        {
            header: 'Branch',
            accessor: 'branch',
            width: '20%',
            render: (s) => (
                <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{s.branch?.branchCode || 'N/A'}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{s.branch?.address || ''}</div>
                </div>
            )
        },
        { header: 'Date of Visit', accessor: 'dateOfVisit', width: '15%', render: (s) => <span className="text-gray-600 dark:text-gray-400">{s.dateOfVisit || 'N/A'}</span> },
        { header: 'Joint Visit', accessor: 'jointVisit', width: '10%', render: (s) => <span className="text-gray-900 dark:text-gray-100">{s.jointVisit || 'N/A'}</span> },
        { header: 'Status', accessor: 'status', width: '10%', render: (s) => getStatusBadge(s.status) },
        {
            header: 'Actions',
            align: 'right',
            render: (survey) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="secondary"
                        className="!px-3 !py-1.5 text-xs"
                        onClick={() => openDetailsModal(survey)}
                    >
                        <Icon icon={FiEye} className="mr-1 h-3 w-3" />
                        View
                    </Button>
                    <Button
                        variant="secondary"
                        className="!px-3 !py-1.5 text-xs"
                        onClick={() => openEmailModal(survey)}
                    >
                        <Icon icon={FiMail} className="mr-1 h-3 w-3" />
                        Email
                    </Button>
                    <Button
                        variant="secondary"
                        className="!px-3 !py-1.5 text-xs"
                        onClick={() => downloadItemPDF(survey)}
                    >
                        <Icon icon={FiDownload} className="mr-1 h-3 w-3" />
                        PDF
                    </Button>
                </div>
            )
        }
    ];

    const complaintColumns: Column<Complaint>[] = [
        { header: 'Complaint ID', accessor: 'complaintId', width: '15%', render: (c) => <span className="font-medium text-gray-900 dark:text-gray-100">{c.complaintId || c.id}</span> },
        { header: 'Issue Type', accessor: 'issueType', width: '15%', render: (c) => <span className="text-gray-900 dark:text-gray-100">{c.issueType || 'N/A'}</span> },
        { header: 'Related Survey', accessor: 'surveyData', width: '15%', render: (c) => <span className="text-gray-600 dark:text-gray-400">{c.surveyData?.surveyId || 'N/A'}</span> },
        { header: 'Branch', width: '15%', render: (c) => <span className="text-gray-900 dark:text-gray-100">{c.surveyData?.branch?.branchCode || 'N/A'}</span> },
        { header: 'Status', accessor: 'status', width: '10%', render: (c) => getStatusBadge(c.status) },
        {
            header: 'Actions',
            align: 'right',
            render: (complaint) => (
                <div className="flex items-center justify-end gap-2">
                    <Button
                        variant="secondary"
                        className="!px-3 !py-1.5 text-xs"
                        onClick={() => openDetailsModal(complaint)}
                    >
                        <Icon icon={FiEye} className="mr-1 h-3 w-3" />
                        View
                    </Button>
                    <Button
                        variant="secondary"
                        className="!px-3 !py-1.5 text-xs"
                        onClick={() => openEmailModal(complaint)}
                    >
                        <Icon icon={FiMail} className="mr-1 h-3 w-3" />
                        Email
                    </Button>
                    <Button
                        variant="secondary"
                        className="!px-3 !py-1.5 text-xs"
                        onClick={() => downloadItemPDF(complaint)}
                    >
                        <Icon icon={FiDownload} className="mr-1 h-3 w-3" />
                        PDF
                    </Button>
                </div>
            )
        }
    ];

    const paginatedSurveys = filteredSurveys.slice((currentPage - 1) * perPage, currentPage * perPage);
    const paginatedComplaints = filteredComplaints.slice((currentPage - 1) * perPage, currentPage * perPage);

    // Generate Survey ID
    const generateSurveyId = () => {
        if (!psoName || psoName.length < 2) {
            const timestamp = Date.now().toString().slice(-4);
            return `SR-XX-${timestamp}`;
        }
        const psoInitials = psoName.substring(0, 2).toUpperCase();
        const timestamp = Date.now().toString().slice(-4);
        return `SR-${psoInitials}-${timestamp}`;
    };

    // Get current PSO ID
    const currentPsoId = pso?.authUid || uid || '';

    // Filter branches based on search term and PSO ID match
    const filteredBranches = branches.filter((branch: any) => {
        // MIS incharge and bank users see all branches (no PSO filter)
        if (!isBankUser && !isMISIncharge) {
            // First filter by PSO ID - only show branches assigned to this PSO
            if (branch.psoId && currentPsoId && branch.psoId !== currentPsoId) {
                return false;
            }
        }

        // Then filter by search term
        if (branchSearchTerm) {
            const searchLower = branchSearchTerm.toLowerCase();
            const branchCode = (branch.branchCode || '').toLowerCase();
            const branchName = (branch.branchName || '').toLowerCase();
            const address = (branch.address || '').toLowerCase();
            return branchCode.includes(searchLower) || branchName.includes(searchLower) || address.includes(searchLower);
        }

        return true;
    });

    useEffect(() => {
        if (!uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                // Get companyId first
                const fetchedCompanyId = await getCompanyId(uid, isBankUser);
                if (fetchedCompanyId) {
                    setCompanyId(fetchedCompanyId);

                    // Fetch branches for the add survey modal
                    try {
                        const branchSnap = await getDocs(collection(db, "banks", fetchedCompanyId, "branch"));
                        const allBranches = branchSnap.docs.map(doc => ({
                            id: doc.id,
                            ...doc.data()
                        }));

                        // Filter branches by PSO ID if user is a PSO; MIS incharge sees all
                        const currentPsoId = pso?.authUid || uid || '';
                        const filteredBranchesList = (isBankUser || isMISIncharge)
                            ? allBranches
                            : allBranches.filter((branch: any) => {
                                // Show branches that match this PSO's ID
                                return branch.psoId === currentPsoId;
                            });

                        setBranches(filteredBranchesList);
                    } catch (error) {
                        console.error('Error fetching branches:', error);
                    }
                }

                const [surveysData, complaintsData] = await Promise.all([
                    fetchSurveys(isBankUser || isMISIncharge),
                    fetchComplaints(isBankUser || isMISIncharge)
                ]);

                setComplaints(complaintsData);

                // Calculate status for each survey based on complaints
                const calculateStatus = (surveyId: string): 'Pending' | 'Resolved' => {
                    const surveyComplaints = complaintsData.filter(c => c.surveyId === surveyId);

                    // If no complaints exist, survey is resolved
                    if (surveyComplaints.length === 0) {
                        return 'Resolved';
                    }

                    // Check if any complaints are pending
                    const pendingCount = surveyComplaints.filter(c => {
                        const status = c.status?.toLowerCase();
                        return status === 'pending' || !status;
                    }).length;

                    // If any complaints are pending, survey is pending
                    if (pendingCount > 0) {
                        return 'Pending';
                    }

                    // All complaints are resolved
                    return 'Resolved';
                };

                const surveysWithStatus = surveysData.map(survey => ({
                    ...survey,
                    status: calculateStatus(survey.id)
                }));

                setSurveys(surveysWithStatus);
                setFilteredSurveys(surveysWithStatus);
                setFilteredComplaints(complaintsData);
            } catch (err: any) {
                showToast('Failed to load data', 'error');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [uid, isBankUser]);

    const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setToast({ show: true, message, type });
    };


    // Filter surveys
    useEffect(() => {
        let list = surveys;

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(survey => {
                const surveyId = (survey.surveyId || '').toString().toLowerCase();
                const branchCode = (survey.branch?.branchCode || '').toString().toLowerCase();
                const branchAddress = (survey.branch?.address || '').toString().toLowerCase();
                const dateOfVisit = (survey.dateOfVisit || '').toString().toLowerCase();
                return surveyId.includes(term) || branchCode.includes(term) || branchAddress.includes(term) || dateOfVisit.includes(term);
            });
        }

        // Apply date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            list = list.filter(survey => {
                if (!survey.dateOfVisit) return false;
                const surveyDate = new Date(survey.dateOfVisit);
                const surveyDateOnly = new Date(surveyDate.getFullYear(), surveyDate.getMonth(), surveyDate.getDate());

                switch (dateFilter) {
                    case 'daily':
                        return surveyDateOnly.getTime() === today.getTime();
                    case 'weekly':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        return surveyDateOnly >= weekAgo && surveyDateOnly <= today;
                    case 'monthly':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        return surveyDateOnly >= monthAgo && surveyDateOnly <= today;
                    default:
                        return true;
                }
            });
        }

        // Sort by date of visit (newest first)
        list.sort((a, b) => {
            const dateA = a.dateOfVisit ? new Date(a.dateOfVisit).getTime() : 0;
            const dateB = b.dateOfVisit ? new Date(b.dateOfVisit).getTime() : 0;
            return dateB - dateA;
        });

        setFilteredSurveys(list);
    }, [searchTerm, surveys, dateFilter]);

    // Filter complaints
    useEffect(() => {
        let list = complaints;

        // Apply search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            list = list.filter(complaint => {
                const complaintId = (complaint.complaintId || '').toString().toLowerCase();
                const issueType = (complaint.issueType || '').toString().toLowerCase();
                const surveyId = (complaint.surveyData?.surveyId || '').toString().toLowerCase();
                const branchCode = (complaint.surveyData?.branch?.branchCode || '').toString().toLowerCase();
                const status = (complaint.status || '').toString().toLowerCase();
                return complaintId.includes(term) || issueType.includes(term) || surveyId.includes(term) || branchCode.includes(term) || status.includes(term);
            });
        }

        // Apply date filter
        if (dateFilter !== 'all') {
            const now = new Date();
            const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

            list = list.filter(complaint => {
                if (!complaint.createdAt) return false;
                const complaintDate = complaint.createdAt?.toDate ? complaint.createdAt.toDate() : new Date(complaint.createdAt);
                const complaintDateOnly = new Date(complaintDate.getFullYear(), complaintDate.getMonth(), complaintDate.getDate());

                switch (dateFilter) {
                    case 'daily':
                        return complaintDateOnly.getTime() === today.getTime();
                    case 'weekly':
                        const weekAgo = new Date(today);
                        weekAgo.setDate(today.getDate() - 7);
                        return complaintDateOnly >= weekAgo && complaintDateOnly <= today;
                    case 'monthly':
                        const monthAgo = new Date(today);
                        monthAgo.setMonth(today.getMonth() - 1);
                        return complaintDateOnly >= monthAgo && complaintDateOnly <= today;
                    default:
                        return true;
                }
            });
        }

        // Sort by creation date (newest first)
        list.sort((a, b) => {
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
            return dateB - dateA;
        });

        setFilteredComplaints(list);
    }, [searchTerm, complaints, dateFilter]);

    const openDetailsModal = (item: Survey | Complaint) => {
        if (activeTab === 'surveys') {
            setSelectedSurvey(item as Survey);
            setEditedSurvey(item as Survey);
            setIsEditing(false);
        } else {
            setSelectedComplaint(item as Complaint);
        }
        setShowDetailsModal(true);
    };

    const handleEdit = () => {
        if (selectedSurvey) {
            setIsEditing(true);
            setEditedSurvey({ ...selectedSurvey });
        }
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        if (selectedSurvey) {
            setEditedSurvey(selectedSurvey);
        }
    };

    const resetForm = () => {
        const generatedId = generateSurveyId();
        setFormData({
            surveyId: generatedId,
            dateOfVisit: '',
            branchId: '',
            jointVisit: '',
            jointTeams: '',
            status: 'Pending',
            remarks: '',
            cctv: { status: '', issues: '', otherIssues: '' },
            bams: { status: '', issues: '', otherIssues: '' },
            guarding: { status: '', issues: '', otherIssues: '' },
            customIssues: []
        });
        setBranchSearchTerm('');
        setShowBranchDropdown(false);
    };

    // Update PSO name when component loads
    useEffect(() => {
        if (pso?.name) {
            setPsoName(pso.name);
        } else {
            const storedName = sessionStorage.getItem('psoName');
            if (storedName) {
                setPsoName(storedName);
            }
        }
    }, [pso]);

    // Auto-generate survey ID when modal opens
    useEffect(() => {
        if (showAddModal && psoName) {
            const generatedId = generateSurveyId();
            setFormData(prev => ({ ...prev, surveyId: generatedId }));
        }
    }, [showAddModal, psoName]);

    // Close branch dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (branchDropdownRef.current && !branchDropdownRef.current.contains(event.target as Node)) {
                setShowBranchDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleAddSurvey = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!companyId) {
            showToast('Company ID not found', 'error');
            return;
        }

        setIsSubmitting(true);
        try {
            // Prepare survey data
            const surveyData: Partial<Survey> = {
                surveyId: formData.surveyId,
                dateOfVisit: formData.dateOfVisit,
                branchId: formData.branchId,
                jointVisit: formData.jointVisit,
                jointTeams: formData.jointTeams ? formData.jointTeams.split(',').map(t => t.trim()).filter(t => t) : [],
                status: formData.status,
                remarks: formData.remarks,
                cctv: formData.cctv.status ? {
                    status: formData.cctv.status,
                    issues: formData.cctv.issues ? formData.cctv.issues.split(',').map(i => i.trim()).filter(i => i) : [],
                    otherIssues: formData.cctv.otherIssues ? formData.cctv.otherIssues.split(',').map(i => i.trim()).filter(i => i) : []
                } : undefined,
                bams: formData.bams.status ? {
                    status: formData.bams.status,
                    issues: formData.bams.issues ? formData.bams.issues.split(',').map(i => i.trim()).filter(i => i) : [],
                    otherIssues: formData.bams.otherIssues ? formData.bams.otherIssues.split(',').map(i => i.trim()).filter(i => i) : []
                } : undefined,
                guarding: formData.guarding.status ? {
                    status: formData.guarding.status,
                    issues: formData.guarding.issues ? formData.guarding.issues.split(',').map(i => i.trim()).filter(i => i) : [],
                    otherIssues: formData.guarding.otherIssues ? formData.guarding.otherIssues.split(',').map(i => i.trim()).filter(i => i) : []
                } : undefined,
                customIssues: formData.customIssues.length > 0 ? formData.customIssues : undefined
            };

            await createSurvey(companyId, surveyData);

            // Refresh data
            const [surveysData, complaintsData] = await Promise.all([
                fetchSurveys(isBankUser),
                fetchComplaints(isBankUser)
            ]);

            setComplaints(complaintsData);

            // Recalculate status
            const calculateStatus = (surveyId: string): 'Pending' | 'Resolved' => {
                const surveyComplaints = complaintsData.filter(c => c.surveyId === surveyId);
                if (surveyComplaints.length === 0) {
                    return 'Resolved';
                }
                const pendingCount = surveyComplaints.filter(c => {
                    const status = c.status?.toLowerCase();
                    return status === 'pending' || !status;
                }).length;
                return pendingCount > 0 ? 'Pending' : 'Resolved';
            };

            const surveysWithStatus = surveysData.map(survey => ({
                ...survey,
                status: calculateStatus(survey.id)
            }));

            setSurveys(surveysWithStatus);
            setFilteredSurveys(surveysWithStatus);
            setFilteredComplaints(complaintsData);

            showToast('Survey created successfully', 'success');
            setShowAddModal(false);
            resetForm();
        } catch (error: any) {
            console.error('Error creating survey:', error);
            showToast('Failed to create survey', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!selectedSurvey || !editedSurvey || !companyId) return;
        if (!isBankUser) { // Only PSO can edit
            setUpdating(true);
            try {
                await updateSurvey(companyId, selectedSurvey.id, {
                    status: editedSurvey.status,
                    // Don't update jointVisit or jointTeams - they're read-only
                });

                // Refresh data
                const [surveysData, complaintsData] = await Promise.all([
                    fetchSurveys(isBankUser),
                    fetchComplaints(isBankUser)
                ]);

                setComplaints(complaintsData);

                // Recalculate status for each survey based on complaints
                const calculateStatus = (surveyId: string): 'Pending' | 'Resolved' => {
                    const surveyComplaints = complaintsData.filter(c => c.surveyId === surveyId);
                    if (surveyComplaints.length === 0) {
                        return 'Resolved';
                    }
                    const pendingCount = surveyComplaints.filter(c => {
                        const status = c.status?.toLowerCase();
                        return status === 'pending' || !status;
                    }).length;
                    return pendingCount > 0 ? 'Pending' : 'Resolved';
                };

                const surveysWithStatus = surveysData.map(survey => ({
                    ...survey,
                    status: calculateStatus(survey.id)
                }));

                setSurveys(surveysWithStatus);
                setFilteredSurveys(surveysWithStatus);

                // Update selected survey
                const updated = surveysWithStatus.find(s => s.id === selectedSurvey.id);
                if (updated) {
                    setSelectedSurvey(updated);
                    setEditedSurvey(updated);
                }

                setIsEditing(false);
                showToast('Survey updated successfully', 'success');
            } catch (err: any) {
                console.error('Error updating survey:', err);
                showToast('Failed to update survey', 'error');
            } finally {
                setUpdating(false);
            }
        }
    };

    const openEmailModal = (item: Survey | Complaint) => {
        if (activeTab === 'surveys') {
            setSelectedSurvey(item as Survey);
        } else {
            setSelectedComplaint(item as Complaint);
        }
        setEmailRecipients('');
        setShowEmailModal(true);
    };

    const handleSendEmail = async () => {
        const selectedItem = activeTab === 'surveys' ? selectedSurvey : selectedComplaint;
        if (!selectedItem) return;
        if (!emailRecipients.trim()) {
            showToast('Please enter at least one email address', 'warning');
            return;
        }

        // Validate email addresses
        const emailValidation = validateEmails(emailRecipients);
        if (!emailValidation.isValid) {
            showToast(`Invalid email addresses: ${emailValidation.invalid.join(', ')}`, 'error');
            return;
        }

        setIsSendingEmail(true);
        try {
            let emailData;
            if (activeTab === 'surveys') {
                const survey = selectedItem as Survey;
                // Get PSO name from sessionStorage or use default
                const psoName = sessionStorage.getItem('psoName') || 'PSO Team';
                const companyDetails = {
                    companyName: 'HBL Support',
                    imgUrl: ''
                };
                emailData = {
                    to: emailValidation.valid,
                    subject: `HBL Support - Survey Report - ${survey.surveyId || 'N/A'}`,
                    body: formatSurveyEmailBody(survey, companyDetails, psoName),
                    isHtml: true,
                    surveyId: survey.surveyId,
                    surveyType: 'survey'
                };
            } else {
                const complaint = selectedItem as Complaint;
                const psoName = sessionStorage.getItem('psoName') || 'PSO Team';
                const companyDetails = {
                    companyName: 'HBL Support',
                    imgUrl: ''
                };
                emailData = {
                    to: emailValidation.valid,
                    subject: `HBL Support - Complaint Report - ${complaint.complaintId || complaint.id}`,
                    body: formatComplaintEmailBody(complaint, companyDetails, psoName),
                    isHtml: true,
                    complaintId: complaint.complaintId || complaint.id,
                    issueType: complaint.issueType
                };
            }

            const results = await sendEmailsWithErrorHandling(emailData);

            // Prepare result message
            let resultMessage = "";
            if (results.successful.length > 0) {
                resultMessage += `Successfully sent to ${results.successful.length} recipient(s): ${results.successful.map(r => r.email).join(', ')}`;
            }

            if (results.failed.length > 0) {
                if (resultMessage) resultMessage += "\n\n";
                resultMessage += `Failed to send to ${results.failed.length} recipient(s):\n`;
                results.failed.forEach(failure => {
                    resultMessage += `• ${failure.email}: ${failure.error}\n`;
                });
            }

            showToast(resultMessage, results.failed.length === 0 ? 'success' : 'warning');

            if (results.failed.length === 0) {
                setShowEmailModal(false);
                setEmailRecipients('');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            showToast('Error sending email. Please try again.', 'error');
        } finally {
            setIsSendingEmail(false);
        }
    };

    const exportToExcel = () => {
        const data = activeTab === 'surveys' ? filteredSurveys : filteredComplaints;

        if (data.length === 0) {
            showToast(`No ${activeTab} to export`, 'warning');
            return;
        }

        let worksheetData;
        if (activeTab === 'surveys') {
            worksheetData = (data as Survey[]).map(survey => ({
                'Survey ID': survey.surveyId || 'N/A',
                'Branch Code': survey.branch?.branchCode || 'N/A',
                'Branch Address': survey.branch?.address || 'N/A',
                'Date of Visit': survey.dateOfVisit || 'N/A',
                'Joint Visit': survey.jointVisit || 'N/A',
                'Status': survey.status || 'N/A',
                'CCTV Status': survey.cctv?.status || 'N/A',
                'BAMS Status': survey.bams?.status || 'N/A',
                'Guarding Status': survey.guarding?.status || 'N/A',
                'Remarks': survey.remarks || 'N/A',
            }));
        } else {
            worksheetData = (data as Complaint[]).map(complaint => ({
                'Complaint ID': complaint.complaintId || complaint.id,
                'Issue Type': complaint.issueType || 'N/A',
                'Related Survey ID': complaint.surveyData?.surveyId || 'N/A',
                'Branch Code': complaint.surveyData?.branch?.branchCode || 'N/A',
                'Branch Address': complaint.surveyData?.branch?.address || 'N/A',
                'Date of Visit': complaint.surveyData?.dateOfVisit || 'N/A',
                'Status': complaint.status || 'N/A',
                'Issues': complaint.issues?.join('; ') || 'N/A',
                'Description': complaint.description || 'N/A',
                'Resolution': complaint.resolution || 'N/A',
            }));
        }

        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, activeTab === 'surveys' ? 'Surveys' : 'Complaints');

        const fileName = `${activeTab === 'surveys' ? 'Survey' : 'Complaint'}_Reports_${dateFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
        showToast('Excel file exported successfully', 'success');
    };

    const exportToPDF = () => {
        const data = activeTab === 'surveys' ? filteredSurveys : filteredComplaints;

        if (data.length === 0) {
            showToast(`No ${activeTab} to export`, 'warning');
            return;
        }

        const doc = new jsPDF();
        let yPosition = 20;

        // Professional Header
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(44, 62, 80);
        doc.text('ADVANCE GUARD SECURITY SERVICES', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(16);
        doc.setTextColor(52, 73, 94);
        doc.text(`${activeTab === 'surveys' ? 'SURVEY' : 'COMPLAINT'} REPORTS`, 20, yPosition);
        yPosition += 15;

        // Report Information
        doc.setFillColor(248, 249, 250);
        doc.rect(15, yPosition - 5, 180, 35, 'F');
        doc.setDrawColor(189, 195, 199);
        doc.rect(15, yPosition - 5, 180, 35, 'S');

        doc.setFontSize(10);
        doc.setTextColor(52, 73, 94);
        doc.setFont('helvetica', 'bold');
        doc.text('REPORT INFORMATION', 20, yPosition);
        yPosition += 6;

        doc.setFont('helvetica', 'normal');
        doc.text(`Period: ${dateFilter === 'all' ? 'All Time' : dateFilter.charAt(0).toUpperCase() + dateFilter.slice(1)}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Total ${activeTab === 'surveys' ? 'Surveys' : 'Complaints'}: ${data.length}`, 20, yPosition);
        yPosition += 4;
        doc.text(`Generated: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, yPosition);
        yPosition += 15;

        // Table headers
        let headers: string[];
        let columnWidths: number[];

        if (activeTab === 'surveys') {
            headers = ['Survey ID', 'Branch', 'Date of Visit', 'Status'];
            columnWidths = [40, 60, 35, 35];
        } else {
            headers = ['Complaint ID', 'Issue Type', 'Survey ID', 'Branch', 'Status'];
            columnWidths = [35, 40, 35, 35, 35];
        }

        const startX = 15;

        // Table header
        doc.setFillColor(52, 152, 219);
        doc.rect(startX, yPosition - 3, columnWidths.reduce((a, b) => a + b, 0), 8, 'F');

        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        let xPosition = startX + 2;
        headers.forEach((header, index) => {
            doc.text(header, xPosition, yPosition);
            xPosition += columnWidths[index];
        });
        yPosition += 10;

        // Table data
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);

        data.forEach((item, index) => {
            if (yPosition > 270) {
                doc.addPage();
                yPosition = 20;
            }

            if (index % 2 === 0) {
                doc.setFillColor(248, 249, 250);
                doc.rect(startX, yPosition - 3, columnWidths.reduce((a, b) => a + b, 0), 6, 'F');
            }

            let rowData: string[];
            if (activeTab === 'surveys') {
                const survey = item as Survey;
                rowData = [
                    survey.surveyId || 'N/A',
                    survey.branch?.branchCode || 'N/A',
                    survey.dateOfVisit || 'N/A',
                    survey.status || 'N/A'
                ];
            } else {
                const complaint = item as Complaint;
                rowData = [
                    complaint.complaintId || complaint.id,
                    complaint.issueType || 'N/A',
                    complaint.surveyData?.surveyId || 'N/A',
                    complaint.surveyData?.branch?.branchCode || 'N/A',
                    complaint.status || 'N/A'
                ];
            }

            doc.setTextColor(44, 62, 80);
            xPosition = startX + 2;
            rowData.forEach((data, colIndex) => {
                const text = data.toString().substring(0, columnWidths[colIndex] / 1.5);
                doc.text(text, xPosition, yPosition);
                xPosition += columnWidths[colIndex];
            });
            yPosition += 6;
        });

        // Footer
        yPosition += 10;
        doc.setFontSize(8);
        doc.setTextColor(127, 140, 141);
        doc.setFont('helvetica', 'italic');
        doc.text('Generated by Advance Guard Security Management System', 20, yPosition);

        const fileName = `${activeTab === 'surveys' ? 'Survey' : 'Complaint'}_Reports_${dateFilter}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        showToast('PDF file exported successfully', 'success');
    };

    const downloadItemPDF = (item: Survey | Complaint) => {
        const doc = new jsPDF();
        let currentY = 20;

        // Header
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(`HBL Support - ${activeTab === 'surveys' ? 'Survey' : 'Complaint'} Report`, 14, currentY + 8);

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        const currentDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
        doc.text(`Date: ${currentDate}`, 14, currentY + 25);

        currentY += 35;

        if (activeTab === 'surveys') {
            const survey = item as Survey;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('SURVEY DETAILS', 14, currentY);
            currentY += 10;

            const surveyDetails = [
                ['Survey ID:', survey.surveyId || 'N/A'],
                ['Branch Code:', survey.branch?.branchCode || 'N/A'],
                ['Branch Address:', survey.branch?.address || 'N/A'],
                ['Date of Visit:', survey.dateOfVisit || 'N/A'],
                ['Joint Visit:', survey.jointVisit || 'N/A'],
                ['Status:', survey.status || 'N/A'],
            ];

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            surveyDetails.forEach(([label, value]) => {
                doc.setFont('helvetica', 'bold');
                doc.text(label, 14, currentY);
                doc.setFont('helvetica', 'normal');
                const lines = doc.splitTextToSize(value, 180);
                doc.text(lines, 60, currentY);
                currentY += lines.length * 7 + 3;
            });

            if (survey.cctv || survey.bams || survey.guarding) {
                currentY += 5;
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.text('STATUS DETAILS', 14, currentY);
                currentY += 10;

                const statusData: any[][] = [['Category', 'Status', 'Issues']];

                if (survey.cctv) {
                    const issues = [...(survey.cctv.issues || []), ...(survey.cctv.otherIssues || [])].join(', ') || 'None';
                    statusData.push(['CCTV', survey.cctv.status || 'N/A', issues]);
                }
                if (survey.bams) {
                    const issues = [...(survey.bams.issues || []), ...(survey.bams.otherIssues || [])].join(', ') || 'None';
                    statusData.push(['BAMS', survey.bams.status || 'N/A', issues]);
                }
                if (survey.guarding) {
                    const issues = [...(survey.guarding.issues || []), ...(survey.guarding.otherIssues || [])].join(', ') || 'None';
                    statusData.push(['Guarding', survey.guarding.status || 'N/A', issues]);
                }

                autoTable(doc, {
                    startY: currentY,
                    head: [statusData[0]],
                    body: statusData.slice(1),
                    styles: { fontSize: 9 },
                    headStyles: { fillColor: [52, 152, 219], textColor: 255, fontStyle: 'bold' },
                });
                currentY = (doc as any).lastAutoTable.finalY + 10;
            }

            if (survey.remarks) {
                currentY += 5;
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('REMARKS', 14, currentY);
                currentY += 10;

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                const lines = doc.splitTextToSize(survey.remarks, 180);
                doc.text(lines, 14, currentY);
                currentY += lines.length * 7 + 10;
            }
        } else {
            const complaint = item as Complaint;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.text('COMPLAINT DETAILS', 14, currentY);
            currentY += 10;

            const complaintDetails = [
                ['Complaint ID:', complaint.complaintId || complaint.id],
                ['Issue Type:', complaint.issueType || 'N/A'],
                ['Survey ID:', complaint.surveyData?.surveyId || 'N/A'],
                ['Branch Code:', complaint.surveyData?.branch?.branchCode || 'N/A'],
                ['Branch Address:', complaint.surveyData?.branch?.address || 'N/A'],
                ['Date of Visit:', complaint.surveyData?.dateOfVisit || 'N/A'],
                ['Status:', complaint.status || 'N/A'],
            ];

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            complaintDetails.forEach(([label, value]) => {
                doc.setFont('helvetica', 'bold');
                doc.text(label, 14, currentY);
                doc.setFont('helvetica', 'normal');
                const lines = doc.splitTextToSize(value, 180);
                doc.text(lines, 60, currentY);
                currentY += lines.length * 7 + 3;
            });

            if (complaint.issues && complaint.issues.length > 0) {
                currentY += 5;
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(12);
                doc.text('ISSUES', 14, currentY);
                currentY += 10;

                const issuesData = complaint.issues.map((issue, index) => [String(index + 1), issue]);
                autoTable(doc, {
                    startY: currentY,
                    head: [['Sr. No.', 'Issue']],
                    body: issuesData,
                    styles: { fontSize: 9 },
                    headStyles: { fillColor: [220, 53, 69], textColor: 255, fontStyle: 'bold' },
                });
                currentY = (doc as any).lastAutoTable.finalY + 10;
            }

            if (complaint.description) {
                currentY += 5;
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('DESCRIPTION', 14, currentY);
                currentY += 10;

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                const lines = doc.splitTextToSize(complaint.description, 180);
                doc.text(lines, 14, currentY);
                currentY += lines.length * 7 + 10;
            }

            if (complaint.resolution) {
                currentY += 5;
                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.text('RESOLUTION', 14, currentY);
                currentY += 10;

                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                const lines = doc.splitTextToSize(complaint.resolution, 180);
                doc.text(lines, 14, currentY);
                currentY += lines.length * 7 + 10;
            }
        }

        // Signature
        currentY += 10;
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.text('Signature: _________________', 14, currentY);
        doc.text('Name: _________________', 14, currentY + 10);
        doc.text('Designation: _________________', 14, currentY + 20);

        const fileName = `${activeTab === 'surveys' ? 'Survey' : 'Complaint'}_${activeTab === 'surveys' ? (item as Survey).surveyId : (item as Complaint).complaintId || (item as Complaint).id}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        showToast('PDF downloaded successfully', 'success');
    };



    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <div className="text-center">
                    <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
                    <p className="text-gray-600 dark:text-gray-400">Loading data...</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="p-6">
                {/* Toast */}
                {toast.show && (
                    <div className="pointer-events-none fixed right-4 top-4 z-50">
                        <div className="pointer-events-auto">
                            <Toast
                                message={toast.message}
                                type={toast.type}
                                onClose={() => setToast({ ...toast, show: false })}
                            />
                        </div>
                    </div>
                )}

                {/* Header */}
                <div className="mb-6 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Surveys & Complaints</h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">Manage and track survey operations and complaints</p>
                    </div>
                    {activeTab === 'surveys' && !isBankUser && !isMISIncharge && (
                        <Button
                            onClick={() => setShowAddModal(true)}
                            className="flex items-center gap-2"
                        >
                            <Icon icon={FiPlus} className="h-4 w-4" />
                            Add Survey
                        </Button>
                    )}
                </div>

                {/* Tabs */}
                <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('surveys')}
                            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${activeTab === 'surveys'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            Surveys ({surveys.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('complaints')}
                            className={`whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium ${activeTab === 'complaints'
                                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                                }`}
                        >
                            Complaints ({complaints.length})
                        </button>
                    </nav>
                </div>

                {/* Search and Filter Controls */}
                <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="flex-1 md:max-w-md">
                        <TextField
                            type="text"
                            placeholder={activeTab === 'surveys' ? 'Search by Survey ID, Branch, or Date...' : 'Search by Complaint ID, Issue Type, or Status...'}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            prefix={<Icon icon={FiSearch} />}
                        />
                    </div>
                    <div className="flex gap-3">
                        <div className="w-full md:w-48">
                            <select
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value as 'all' | 'daily' | 'weekly' | 'monthly')}
                                className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                            >
                                <option value="all" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">All Time</option>
                                <option value="daily" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Daily</option>
                                <option value="weekly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Weekly</option>
                                <option value="monthly" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Monthly</option>
                            </select>
                        </div>
                        <Button variant="secondary" onClick={exportToExcel} className="flex items-center gap-2">
                            <Icon icon={FiFile} className="h-4 w-4" />
                            Export Excel
                        </Button>
                        <Button variant="secondary" onClick={exportToPDF} className="flex items-center gap-2">
                            <Icon icon={FiDownload} className="h-4 w-4" />
                            Export PDF
                        </Button>
                    </div>
                </div>

                {/* Surveys Table */}
                {activeTab === 'surveys' && (
                    <Table
                        data={paginatedSurveys}
                        columns={surveyColumns}
                        keyExtractor={(item) => item.id}
                        loading={loading}
                        emptyMessage={searchTerm || dateFilter !== 'all' ? 'Try adjusting your search or filter' : 'Get started by creating your first survey'}
                        pagination={{
                            currentPage,
                            totalPages: Math.ceil(filteredSurveys.length / perPage),
                            onPageChange: setCurrentPage
                        }}
                    />
                )}

                {/* Complaints Table */}
                {activeTab === 'complaints' && (
                    <Table
                        data={paginatedComplaints}
                        columns={complaintColumns}
                        keyExtractor={(item) => item.id}
                        loading={loading}
                        emptyMessage={searchTerm || dateFilter !== 'all' ? 'Try adjusting your search or filter' : 'No complaints have been registered yet'}
                        pagination={{
                            currentPage,
                            totalPages: Math.ceil(filteredComplaints.length / perPage),
                            onPageChange: setCurrentPage
                        }}
                    />
                )}
            </div>

            {/* Details Modal */}
            {showDetailsModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                {activeTab === 'surveys' ? 'Survey Details' : 'Complaint Details'}
                            </h3>
                            <div className="flex items-center gap-2">
                                {activeTab === 'surveys' && selectedSurvey && !isBankUser && (
                                    <>
                                        {!isEditing ? (
                                            <Button
                                                variant="secondary"
                                                className="!px-3 !py-1.5 text-xs"
                                                onClick={handleEdit}
                                            >
                                                <Icon icon={FiEdit2} className="mr-1 h-3 w-3" />
                                                Edit
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="secondary"
                                                    className="!px-3 !py-1.5 text-xs"
                                                    onClick={handleCancelEdit}
                                                    disabled={updating}
                                                >
                                                    <Icon icon={FiX} className="mr-1 h-3 w-3" />
                                                    Cancel
                                                </Button>
                                                <Button
                                                    className="!px-3 !py-1.5 text-xs"
                                                    onClick={handleSaveEdit}
                                                    disabled={updating}
                                                >
                                                    <Icon icon={FiSave} className="mr-1 h-3 w-3" />
                                                    {updating ? 'Saving...' : 'Save'}
                                                </Button>
                                            </>
                                        )}
                                    </>
                                )}
                                <button
                                    onClick={() => {
                                        setShowDetailsModal(false);
                                        setSelectedSurvey(null);
                                        setSelectedComplaint(null);
                                        setIsEditing(false);
                                        setEditedSurvey(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                        <div className="space-y-4">
                            {activeTab === 'surveys' && selectedSurvey && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Survey ID</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedSurvey.surveyId || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Visit</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedSurvey.dateOfVisit || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedSurvey.branch?.branchCode || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Address</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedSurvey.branch?.address || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Joint Visit</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedSurvey.jointVisit || 'N/A'}</p>
                                        </div>
                                        {selectedSurvey.jointVisit === 'Yes' && selectedSurvey.jointTeams && selectedSurvey.jointTeams.length > 0 && (
                                            <div>
                                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Joint Teams</label>
                                                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedSurvey.jointTeams?.join(', ') || 'N/A'}</p>
                                            </div>
                                        )}
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                            {isEditing ? (
                                                <select
                                                    value={editedSurvey?.status || ''}
                                                    onChange={(e) => setEditedSurvey({ ...editedSurvey, status: e.target.value })}
                                                    className="mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                                >
                                                    <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Status...</option>
                                                    <option value="Pending" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Pending</option>
                                                    <option value="Resolved" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Resolved</option>
                                                </select>
                                            ) : (
                                                <div>
                                                    <p className="mt-1">{getStatusBadge(selectedSurvey.status)}</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {(selectedSurvey.cctv || selectedSurvey.bams || selectedSurvey.guarding) && (
                                        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                            <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Status Details</h4>
                                            <div className="space-y-3">
                                                {selectedSurvey.cctv && (
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-200">CCTV</label>
                                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                            Status: {selectedSurvey.cctv.status || 'N/A'}
                                                            {selectedSurvey.cctv.issues && selectedSurvey.cctv.issues.length > 0 && (
                                                                <span className="ml-2">• Issues: {selectedSurvey.cctv.issues.join(', ')}</span>
                                                            )}
                                                            {selectedSurvey.cctv.otherIssues && selectedSurvey.cctv.otherIssues.length > 0 && (
                                                                <span className="ml-2">• Other: {selectedSurvey.cctv.otherIssues.join(', ')}</span>
                                                            )}
                                                        </p>
                                                    </div>
                                                )}
                                                {selectedSurvey.bams && (
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-200">BAMS</label>
                                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                            Status: {selectedSurvey.bams.status || 'N/A'}
                                                            {selectedSurvey.bams.issues && selectedSurvey.bams.issues.length > 0 && (
                                                                <span className="ml-2">• Issues: {selectedSurvey.bams.issues.join(', ')}</span>
                                                            )}
                                                            {selectedSurvey.bams.otherIssues && selectedSurvey.bams.otherIssues.length > 0 && (
                                                                <span className="ml-2">• Other: {selectedSurvey.bams.otherIssues.join(', ')}</span>
                                                            )}
                                                        </p>
                                                    </div>
                                                )}
                                                {selectedSurvey.guarding && (
                                                    <div>
                                                        <label className="text-xs font-semibold text-gray-700 dark:text-gray-200">Guarding</label>
                                                        <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                                                            Status: {selectedSurvey.guarding.status || 'N/A'}
                                                            {selectedSurvey.guarding.issues && selectedSurvey.guarding.issues.length > 0 && (
                                                                <span className="ml-2">• Issues: {selectedSurvey.guarding.issues.join(', ')}</span>
                                                            )}
                                                            {selectedSurvey.guarding.otherIssues && selectedSurvey.guarding.otherIssues.length > 0 && (
                                                                <span className="ml-2">• Other: {selectedSurvey.guarding.otherIssues.join(', ')}</span>
                                                            )}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    {selectedSurvey.customIssues && selectedSurvey.customIssues.length > 0 && (
                                        <div className="mt-4">
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Custom Issues</label>
                                            <div className="mt-2 space-y-2">
                                                {selectedSurvey.customIssues.map((issue, index) => (
                                                    <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                                        <p className="font-medium text-gray-900 dark:text-gray-100">{issue.heading || 'Issue'}</p>
                                                        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{issue.description || 'N/A'}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {selectedSurvey.remarks && (
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Remarks</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedSurvey.remarks}</p>
                                        </div>
                                    )}
                                </>
                            )}
                            {activeTab === 'complaints' && selectedComplaint && (
                                <>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Complaint ID</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.complaintId || selectedComplaint.id}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Issue Type</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.issueType || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Related Survey ID</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.surveyData?.surveyId || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Code</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.surveyData?.branch?.branchCode || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Branch Address</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.surveyData?.branch?.address || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Date of Visit</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.surveyData?.dateOfVisit || 'N/A'}</p>
                                        </div>
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Status</label>
                                            <p className="mt-1">{getStatusBadge(selectedComplaint.status)}</p>
                                        </div>
                                    </div>
                                    {selectedComplaint.issues && selectedComplaint.issues.length > 0 && (
                                        <div className="mt-4">
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Issues</label>
                                            <ul className="mt-2 list-inside list-disc space-y-1">
                                                {selectedComplaint.issues.map((issue, index) => (
                                                    <li key={index} className="text-sm text-gray-900 dark:text-gray-100">{issue}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {selectedComplaint.customIssues && selectedComplaint.customIssues.length > 0 && (
                                        <div className="mt-4">
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Custom Issues</label>
                                            <div className="mt-2 space-y-2">
                                                {selectedComplaint.customIssues.map((issue, index) => (
                                                    <div key={index} className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
                                                        <p className="font-medium text-gray-900 dark:text-gray-100">{issue.heading || 'Issue'}</p>
                                                        <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{issue.description || 'N/A'}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {selectedComplaint.description && (
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Description</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.description}</p>
                                        </div>
                                    )}
                                    {selectedComplaint.resolution && (
                                        <div>
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-200">Resolution</label>
                                            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedComplaint.resolution}</p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="mt-6 flex justify-end gap-3">
                            <Button variant="secondary" onClick={() => {
                                const item = activeTab === 'surveys' ? selectedSurvey : selectedComplaint;
                                if (item) {
                                    downloadItemPDF(item);
                                }
                            }}>
                                <Icon icon={FiDownload} className="mr-2 h-4 w-4" />
                                Download PDF
                            </Button>
                            {!isEditing && (
                                <Button variant="secondary" onClick={() => {
                                    setShowDetailsModal(false);
                                    setSelectedSurvey(null);
                                    setSelectedComplaint(null);
                                    setIsEditing(false);
                                    setEditedSurvey(null);
                                }}>
                                    Close
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Email Modal */}
            {(selectedSurvey || selectedComplaint) && (
                <div className={`fixed inset-0 z-50 ${showEmailModal ? 'block' : 'hidden'}`}>
                    <div className="flex min-h-full items-center justify-center bg-black/50 p-4">
                        <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                    Send {activeTab === 'surveys' ? 'Survey' : 'Complaint'} Email
                                </h3>
                                <button
                                    onClick={() => {
                                        setShowEmailModal(false);
                                        setEmailRecipients('');
                                    }}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                >
                                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="space-y-4">
                                <div>
                                    <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Email Addresses (comma, semicolon, or new line separated)
                                    </label>
                                    <textarea
                                        rows={3}
                                        placeholder="Enter email addresses separated by commas"
                                        value={emailRecipients}
                                        onChange={(e) => setEmailRecipients(e.target.value)}
                                        disabled={isSendingEmail}
                                        className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm outline-none transition text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                    />
                                </div>
                                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
                                    <p className="text-xs text-blue-800 dark:text-blue-200">
                                        <strong>Info:</strong> {activeTab === 'surveys'
                                            ? `Survey ${selectedSurvey?.surveyId || 'N/A'}`
                                            : `Complaint ${selectedComplaint?.complaintId || selectedComplaint?.id || 'N/A'}`} will be sent to the recipients.
                                    </p>
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end gap-3">
                                <Button variant="secondary" onClick={() => {
                                    setShowEmailModal(false);
                                    setEmailRecipients('');
                                }} disabled={isSendingEmail}>
                                    Cancel
                                </Button>
                                <Button onClick={handleSendEmail} disabled={isSendingEmail || !emailRecipients.trim()}>
                                    {isSendingEmail ? 'Sending...' : 'Send Email'}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Survey Modal */}
            {showAddModal && (
                <Modal
                    isOpen={showAddModal}
                    onClose={() => {
                        setShowAddModal(false);
                        resetForm();
                    }}
                    title="Add New Survey"
                    size="xl"
                >
                    <form onSubmit={handleAddSurvey} className="p-6">
                        <div className="space-y-6">
                            {/* Basic Information */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiCalendar} className="h-5 w-5" />
                                    Basic Information
                                </h3>
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Survey ID <span className="text-red-500">*</span>
                                        </label>
                                        <div className="flex gap-2">
                                            <TextField
                                                value={formData.surveyId}
                                                onChange={(e) => setFormData({ ...formData, surveyId: e.target.value })}
                                                placeholder="Survey ID will be auto-generated"
                                                required
                                                className="flex-1 bg-gray-50 dark:bg-gray-800"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, surveyId: generateSurveyId() })}
                                                className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-600 dark:bg-black/30 dark:text-gray-300 dark:hover:bg-gray-700"
                                                title="Regenerate Survey ID"
                                            >
                                                <Icon icon={FiRefreshCw} className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Date of Visit *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.dateOfVisit}
                                            onChange={(e) => setFormData({ ...formData, dateOfVisit: e.target.value })}
                                            required
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                        />
                                    </div>
                                    <div className="relative" ref={branchDropdownRef}>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Branch <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <TextField
                                                value={branchSearchTerm || branches.find(b => b.id === formData.branchId)?.branchCode || ''}
                                                onChange={(e) => {
                                                    setBranchSearchTerm(e.target.value);
                                                    setShowBranchDropdown(true);
                                                    if (!e.target.value) {
                                                        setFormData({ ...formData, branchId: '' });
                                                    }
                                                }}
                                                onFocus={() => setShowBranchDropdown(true)}
                                                placeholder="Search by branch code or name..."
                                                prefix={<Icon icon={FiSearch} className="text-gray-400" />}
                                                required={!formData.branchId}
                                                className="bg-white dark:bg-black/30"
                                            />
                                            {showBranchDropdown && filteredBranches.length > 0 && (
                                                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                                    {filteredBranches.map(branch => (
                                                        <button
                                                            key={branch.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setFormData({ ...formData, branchId: branch.id });
                                                                setBranchSearchTerm(branch.branchCode || '');
                                                                setShowBranchDropdown(false);
                                                            }}
                                                            className={`w-full px-4 py-3 text-left text-sm transition hover:bg-blue-50 dark:hover:bg-gray-700 ${formData.branchId === branch.id ? 'bg-blue-100 dark:bg-blue-900/30' : ''
                                                                }`}
                                                        >
                                                            <div className="font-medium text-gray-900 dark:text-gray-100">
                                                                {branch.branchCode || branch.id}
                                                                {branch.branchName && ` - ${branch.branchName}`}
                                                            </div>
                                                            {branch.address && (
                                                                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                                    {branch.address}
                                                                </div>
                                                            )}
                                                        </button>
                                                    ))}
                                                </div>
                                            )}
                                            {showBranchDropdown && branchSearchTerm && filteredBranches.length === 0 && (
                                                <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white p-4 text-center text-sm text-gray-500 shadow-lg dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
                                                    No branches found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Joint Visit
                                        </label>
                                        <select
                                            value={formData.jointVisit}
                                            onChange={(e) => setFormData({ ...formData, jointVisit: e.target.value })}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                        >
                                            <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select...</option>
                                            <option value="Yes" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Yes</option>
                                            <option value="No" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">No</option>
                                        </select>
                                    </div>
                                    {formData.jointVisit === 'Yes' && (
                                        <div className="md:col-span-2">
                                            <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                                Joint Teams
                                            </label>
                                            <TextField
                                                value={formData.jointTeams}
                                                onChange={(e) => setFormData({ ...formData, jointTeams: e.target.value })}
                                                placeholder="Enter teams separated by commas"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <label className="mb-1 block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                            Status
                                        </label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                        >
                                            <option value="Pending" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Pending</option>
                                            <option value="Resolved" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Resolved</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Status Details */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiClipboard} className="h-5 w-5" />
                                    Status Details
                                </h3>
                                <div className="space-y-4">
                                    {/* CCTV */}
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                        <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-200">CCTV</label>
                                        <div className="space-y-3">
                                            <select
                                                value={formData.cctv.status}
                                                onChange={(e) => setFormData({ ...formData, cctv: { ...formData.cctv, status: e.target.value } })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Status...</option>
                                                <option value="Good" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Good</option>
                                                <option value="Issues" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Issues</option>
                                                <option value="Not Working" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Not Working</option>
                                            </select>
                                            <TextField
                                                value={formData.cctv.issues}
                                                onChange={(e) => setFormData({ ...formData, cctv: { ...formData.cctv, issues: e.target.value } })}
                                                placeholder="Issues (comma separated)"
                                            />
                                            <TextField
                                                value={formData.cctv.otherIssues}
                                                onChange={(e) => setFormData({ ...formData, cctv: { ...formData.cctv, otherIssues: e.target.value } })}
                                                placeholder="Other Issues (comma separated)"
                                            />
                                        </div>
                                    </div>

                                    {/* BAMS */}
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                        <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-200">BAMS</label>
                                        <div className="space-y-3">
                                            <select
                                                value={formData.bams.status}
                                                onChange={(e) => setFormData({ ...formData, bams: { ...formData.bams, status: e.target.value } })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Status...</option>
                                                <option value="Good" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Good</option>
                                                <option value="Issues" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Issues</option>
                                                <option value="Not Working" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Not Working</option>
                                            </select>
                                            <TextField
                                                value={formData.bams.issues}
                                                onChange={(e) => setFormData({ ...formData, bams: { ...formData.bams, issues: e.target.value } })}
                                                placeholder="Issues (comma separated)"
                                            />
                                            <TextField
                                                value={formData.bams.otherIssues}
                                                onChange={(e) => setFormData({ ...formData, bams: { ...formData.bams, otherIssues: e.target.value } })}
                                                placeholder="Other Issues (comma separated)"
                                            />
                                        </div>
                                    </div>

                                    {/* Guarding */}
                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900">
                                        <label className="mb-3 block text-sm font-semibold text-gray-700 dark:text-gray-200">Guarding</label>
                                        <div className="space-y-3">
                                            <select
                                                value={formData.guarding.status}
                                                onChange={(e) => setFormData({ ...formData, guarding: { ...formData.guarding, status: e.target.value } })}
                                                className="w-full rounded-xl border border-gray-300 bg-white py-3 px-4 text-sm font-medium text-gray-900 outline-none transition shadow-sm hover:border-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600 dark:hover:border-gray-500"
                                            >
                                                <option value="" className="bg-white text-gray-500 dark:bg-gray-800 dark:text-gray-400">Select Status...</option>
                                                <option value="Good" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Good</option>
                                                <option value="Issues" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Issues</option>
                                                <option value="Not Working" className="bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100">Not Working</option>
                                            </select>
                                            <TextField
                                                value={formData.guarding.issues}
                                                onChange={(e) => setFormData({ ...formData, guarding: { ...formData.guarding, issues: e.target.value } })}
                                                placeholder="Issues (comma separated)"
                                            />
                                            <TextField
                                                value={formData.guarding.otherIssues}
                                                onChange={(e) => setFormData({ ...formData, guarding: { ...formData.guarding, otherIssues: e.target.value } })}
                                                placeholder="Other Issues (comma separated)"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Custom Issues */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiAlertCircle} className="h-5 w-5" />
                                    Custom Issues
                                </h3>
                                <div className="space-y-3">
                                    {formData.customIssues.map((issue, index) => (
                                        <div key={index} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-600 dark:bg-gray-800">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 space-y-3">
                                                    <div>
                                                        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Issue Heading</label>
                                                        <TextField
                                                            value={issue.heading}
                                                            onChange={(e) => {
                                                                const updated = [...formData.customIssues];
                                                                updated[index] = { ...updated[index], heading: e.target.value };
                                                                setFormData({ ...formData, customIssues: updated });
                                                            }}
                                                            placeholder="Enter issue heading"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400">Issue Description</label>
                                                        <textarea
                                                            value={issue.description}
                                                            onChange={(e) => {
                                                                const updated = [...formData.customIssues];
                                                                updated[index] = { ...updated[index], description: e.target.value };
                                                                setFormData({ ...formData, customIssues: updated });
                                                            }}
                                                            placeholder="Enter issue description"
                                                            rows={3}
                                                            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                                        />
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const updated = formData.customIssues.filter((_, i) => i !== index);
                                                        setFormData({ ...formData, customIssues: updated });
                                                    }}
                                                    className="mt-2 rounded-lg p-2 text-red-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                                                    title="Remove issue"
                                                >
                                                    <Icon icon={FiX} className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, customIssues: [...formData.customIssues, { heading: '', description: '' }] })}
                                        className="w-full rounded-lg border-2 border-dashed border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-600 transition hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-blue-500 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                                    >
                                        <Icon icon={FiPlus} className="mr-2 inline h-4 w-4" />
                                        Add Custom Issue
                                    </button>
                                </div>
                            </div>

                            {/* Remarks */}
                            <div>
                                <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100">
                                    <Icon icon={FiFileText} className="h-5 w-5" />
                                    Remarks
                                </h3>
                                <textarea
                                    value={formData.remarks}
                                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                    placeholder="Enter any additional remarks or notes..."
                                    rows={4}
                                    className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition shadow-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 dark:bg-black/30 dark:text-gray-100 dark:border-gray-600"
                                />
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className="mt-6 flex justify-end gap-3">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Creating...' : 'Create Survey'}
                            </Button>
                        </div>
                    </form>
                </Modal>
            )}
        </>
    );
}

